<template>
  <div class="recebimento-table-container">
    <!-- DataTable -->
    <DataTable
      display-mode="handle_user_device"
      :config="tableConfig"
      :data="recebimentos"
      :refreshButton="{ show: true }"
      @refresh="loadData"
      @edit-click="handleEditClick" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '../../grid/DataTable.vue';
import { useRecebimento } from '../../composables/useRecebimento';
import { useFormasPagamento } from '../../composables/useFormasPagamento';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import type { Recebimentos_GetrecebimentosRow } from '../../contracts/Recebimentos_GetrecebimentosRow';

type Id = number | string;

// Extends the wire-shape contract with a UI-only enrichment field
// (recebimento_forma_pagamento_nome is resolved client-side from formasPagamentoMap).
interface RecebimentoRow extends Recebimentos_GetrecebimentosRow {
  recebimento_forma_pagamento_nome?: string;
}

interface FormaPagamentoApi {
  forma_pagamento_id: number;
  forma_pagamento_nome: string;
}

// Get i18n composable
const { t } = useI18n();
const { fetchRecebimentosByCompromisso } = useRecebimento();
const { listarFormasPagamento } = useFormasPagamento();

// Props
const props = withDefaults(defineProps<{
  compromissoId?: number;
}>(), {
  compromissoId: 0,
});

// Emits
const emit = defineEmits<{
  'edit-recebimento': [recebimentoId: Id];
}>();

// Reactive data
const recebimentos = ref<RecebimentoRow[]>([]);
const formasPagamentoMap = ref<Map<number, string>>(new Map());

// Table configuration
const tableConfig = computed(() => ({
  columns: [
    {
      data: 'recebimento_id',
      title: t('ID'),
      renderType: 'clickable_id',
      minWidth: 80,
      sortable: false
    },
    {
      data: 'recebimento_data',
      title: t('Data do Recebimento'),
      renderType: 'date',
      minWidth: 150,
      sortable: false
    },
    {
      data: 'recebimento_data_cadastro',
      title: t('Data de Cadastro'),
      minWidth: 150,
      sortable: false
    },
    {
      data: 'recebimento_valor',
      title: t('Valor'),
      renderType: 'currency',
      minWidth: 120,
      sortable: false,
      footerType: 'sum',
      footerCurrency: true
    },
    {
      data: 'recebimento_valor_taxas',
      title: t('Taxas'),
      renderType: 'currency',
      minWidth: 100,
      sortable: false,
      footerType: 'sum',
      footerCurrency: true
    },
    {
      data: 'recebimento_valor_liquido',
      title: t('Valor líquido'),
      renderType: 'currency',
      minWidth: 120,
      sortable: false,
      footerType: 'sum',
      footerCurrency: true
    },
    {
      data: 'recebimento_forma_pagamento_nome',
      title: t('Forma de Pagamento'),
      minWidth: 200,
      sortable: false
    }
  ],
  pagination: false,
  sortable: false,
  showFilters: false,
  stripedRows: true,
  showGridlines: true,
  heightMode: 'fixed',
  height: '400px',
  rowSelection: 'single',
  footerLabel: t('Total')
}));

// Methods
const loadFormasPagamento = async (): Promise<void> => {
  try {
    log('RecebimentoTableCompromisso', 'loadFormasPagamento', 'Loading payment methods');
    const formasPagamento = await listarFormasPagamento() as FormaPagamentoApi[];

    // Create a map of ID -> Name for quick lookup
    const map = new Map<number, string>();
    formasPagamento.forEach((fp: FormaPagamentoApi) => {
      map.set(fp.forma_pagamento_id, fp.forma_pagamento_nome);
    });
    formasPagamentoMap.value = map;

    log('RecebimentoTableCompromisso', 'loadFormasPagamento', 'Loaded payment methods', formasPagamentoMap.value.size);
  } catch (error) {
    log('RecebimentoTableCompromisso', 'loadFormasPagamento', 'Error loading payment methods', error);
  }
};

const loadData = async (): Promise<void> => {
  if (!props.compromissoId || props.compromissoId === 0) {
    log('RecebimentoTableCompromisso', 'loadData', 'No compromisso ID, skipping load');
    return;
  }

  log('RecebimentoTableCompromisso', 'loadData', 'Loading data for compromisso', props.compromissoId);

  try {
    const dados = await fetchRecebimentosByCompromisso(props.compromissoId) as RecebimentoRow[];

    // Convert centavos to reais for currency rendering and map forma pagamento ID to name
    recebimentos.value = dados.map((item: RecebimentoRow): RecebimentoRow => ({
      ...item,
      recebimento_valor: (item.recebimento_valor || 0) / 100, // Gross value
      recebimento_valor_taxas: (item.recebimento_valor_taxas || 0) / 100,
      recebimento_valor_liquido: (item.recebimento_valor_liquido || ((item.recebimento_valor || 0) - (item.recebimento_valor_taxas || 0)) || 0) / 100, // Net value with fallback calculation
      recebimento_forma_pagamento_nome: (item.recebimento_forma_pagamento_id !== undefined ? formasPagamentoMap.value.get(item.recebimento_forma_pagamento_id) : undefined) || t('Desconhecido')
    }));

    log('RecebimentoTableCompromisso', 'loadData', 'Data loaded', recebimentos.value.length, 'rows');
  } catch (error) {
    log('RecebimentoTableCompromisso', 'loadData', 'Error loading data', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar recebimentos') + ': ' + message);
    recebimentos.value = [];
  }
};

const handleEditClick = (recebimentoId: Id): void => {
  log('RecebimentoTableCompromisso', 'handleEditClick', 'Edit clicked for ID', recebimentoId);
  // Emit event to parent component to handle editing
  emit('edit-recebimento', recebimentoId);
  // Also emit global event for RecebimentoModal
  eventBus.emit('recebimento.open-modal-edit', recebimentoId);
};

const editItem = (recebimentoId: Id): void => {
  log('RecebimentoTableCompromisso', 'editItem', 'Editing ID', recebimentoId);
  handleEditClick(recebimentoId);
};

// Watch for compromissoId changes to reload data
watch(() => props.compromissoId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    log('RecebimentoTableCompromisso', 'watch:compromissoId', 'Compromisso ID changed, reloading', { newId, oldId });
    loadData();
  }
});

// Event handlers (kept as refs to allow proper unbind)
const handleSpecificRefresh = (): void => {
  log('RecebimentoTableCompromisso', 'event:refresh-recebimento-specific', 'Received');
  loadData();
};

const handleGeneralRefresh = (): void => {
  log('RecebimentoTableCompromisso', 'event:recebimento.refresh', 'Received');
  loadData();
};

// Lifecycle hooks
onMounted(async () => {
  log('RecebimentoTableCompromisso', 'mounted', 'Setting up event listeners and loading initial data');

  // Load formas de pagamento first
  await loadFormasPagamento();

  // Load initial data if compromissoId is provided
  if (props.compromissoId && props.compromissoId !== 0) {
    log('RecebimentoTableCompromisso', 'mounted', 'Loading initial data for compromisso', props.compromissoId);
    loadData();
  }

  // Set up event listeners for this specific compromisso
  const refreshEventName = `refresh-recebimento-${props.compromissoId}`;
  eventBus.on(refreshEventName, handleSpecificRefresh);

  // Also listen for general refresh event
  eventBus.on('recebimento.refresh', handleGeneralRefresh);
});

onBeforeUnmount(() => {
  log('RecebimentoTableCompromisso', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up event listeners
  const refreshEventName = `refresh-recebimento-${props.compromissoId}`;
  eventBus.off(refreshEventName, handleSpecificRefresh);
  eventBus.off('recebimento.refresh', handleGeneralRefresh);
});

// Expose public methods for parent components
defineExpose({
  loadData,
  editItem
});
</script>

<style scoped>
.recebimento-table-container {
  padding: 0;
}
</style>
